import { useState, type ReactNode } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';
import { ClipLoader } from 'react-spinners';
import { Colors } from '~/themes/colors';
import '~/themes/registration.scss';
import { Center } from '../Center';

export interface ILocationResult {
  place_id: string;
  formatted_address: string;
}

export interface ILocationSearchProps {
  address?: string;
  placeholder?: string;
  result: (location: ILocationResult) => void;
}

/**
 * LocationSearch Component
 *
 * A location search input component using Google Places Autocomplete API
 * with US address restrictions and debounced search functionality.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for registration office information. Consider whether
 * this should be moved to a more specific location like:
 * - src/components/registration/LocationSearch
 *
 * If this is truly only used for registration workflows, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <LocationSearch
 *   placeholder="Enter office address"
 *   result={(location) => setOfficeLocation(location)}
 * />
 * ```
 */
export const LocationSearch = ({
  address: propAddress,
  placeholder,
  result,
}: ILocationSearchProps): ReactNode => {
  const [address, setAddress] = useState<string>();

  const handleChange = (newAddress: string): void => {
    setAddress(newAddress);
  };

  const handleSelect = (selectedAddress: string, placeId: string): void => {
    setAddress(selectedAddress);
    result({ place_id: placeId, formatted_address: selectedAddress });
  };

  return (
    <PlacesAutocomplete
      value={propAddress && !address ? propAddress : address}
      onChange={handleChange}
      onSelect={handleSelect}
      debounce={1000}
      searchOptions={{
        componentRestrictions: { country: ['us'] },
        types: ['address'],
      }}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <>
          <input
            data-cy="input-location-search"
            {...getInputProps({
              placeholder,
              className: 'input-field',
              style: {
                width: '100%',
                maxWidth: '-webkit-fill-available',
                marginBottom: 0,
              },
            })}
          />
          <div
            className="autocomplete-dropdown-container"
            style={{
              boxShadow: '0px 2px 4px rgba(111, 131, 141, 0.3)',
              width: 510,
              borderRadius: '5px',
              position: 'absolute',
            }}
          >
            {loading ? (
              <Center backgroundColor={Colors.white} paddingVertical={12}>
                <ClipLoader size="50px" color={Colors.primary_500} loading={loading} />
              </Center>
            ) : (
              suggestions.map((suggestion) => {
                const className = suggestion.active
                  ? 'suggestion-item--active suggest'
                  : 'suggestion-item suggest';
                const style = suggestion.active
                  ? { backgroundColor: 'rgba(62, 176, 202, 1)', cursor: 'pointer' }
                  : { backgroundColor: Colors.white, cursor: 'pointer' };

                return (
                  <div
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style,
                    })}
                    key={suggestion.placeId}
                    data-cy="location-suggestion-item"
                  >
                    {suggestion.description}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </PlacesAutocomplete>
  );
};
